import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Delete, Stop } from "@mui/icons-material";

const BAR_COUNT = 32;
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Indicador de gravação no layout da referência (WhatsApp Web):
 * [Lixeira] [• Tempo] [Ondas] [Parar]
 * Fundo e cores no padrão da aplicação (tema claro).
 */
const RecordingIndicator = ({ durationSeconds = 0, onStop, onDiscard }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        height: 48,
        minHeight: 48,
        maxHeight: 48,
        padding: "0 12px 0 8px",
        borderRadius: "24px",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={onDiscard}
        size="small"
        sx={{
          color: "text.secondary",
          flexShrink: 0,
          padding: "8px",
          "&:hover": { color: "error.main", backgroundColor: "action.hover" },
        }}
        aria-label="Descartar gravação"
      >
        <Delete sx={{ fontSize: 22 }} />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexShrink: 0,
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "error.main",
            animation: "recordingBlink 1.2s ease-in-out infinite",
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontVariantNumeric: "tabular-nums",
            fontSize: "0.9375rem",
          }}
        >
          {formatTime(durationSeconds)}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.4,
          height: 36,
          minWidth: 0,
        }}
      >
        {Array.from({ length: BAR_COUNT }, (_, i) => {
          const seed = (i * 7 + 3) % 5;
          const baseHeight = 10 + seed * 4;
          return (
            <Box
              key={i}
              sx={{
                width: 3,
                height: baseHeight,
                borderRadius: 2,
                backgroundColor: "primary.main",
                animation: "recordingPulse 0.5s ease-in-out infinite",
                animationDelay: `${(i % 6) * 0.08}s`,
                animationDirection: "alternate",
                transformOrigin: "center bottom",
              }}
            />
          );
        })}
      </Box>

      <IconButton
        onClick={onStop}
        size="small"
        sx={{
          color: "primary.main",
          flexShrink: 0,
          padding: "8px",
          "&:hover": { backgroundColor: "action.hover" },
        }}
        aria-label="Parar gravação"
      >
        <Stop sx={{ fontSize: 28 }} />
      </IconButton>
      <style>{`
        @keyframes recordingPulse {
          0% { transform: scaleY(0.3); opacity: 0.8; }
          100% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes recordingBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </Box>
  );
};

export default RecordingIndicator;
